import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  ShieldAlert, AlertTriangle, Trash2, Ban, Loader2, RefreshCw,
  Eye, Building2, User, Bell, CheckCircle2, XCircle, Flag,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ------------------------------------------------------------
const SeverityBadge = ({ severity }) => {
  const cfg = {
    HIGH:   { className: 'bg-red-600 text-white', label: 'HIGH', Icon: AlertTriangle },
    MEDIUM: { className: 'bg-amber-500 text-white', label: 'MEDIUM', Icon: AlertTriangle },
    LOW:    { className: 'bg-slate-500 text-white', label: 'LOW', Icon: Flag },
  }[severity] || { className: 'bg-slate-200 text-slate-700', label: severity || '-', Icon: Flag };
  const { Icon } = cfg;
  return (
    <Badge className={`${cfg.className} gap-1`} data-testid={`severity-${severity}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </Badge>
  );
};

const IncidentChip = ({ label, count, tone }) => (
  <span
    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono ${tone}`}
  >
    {label} <span className="font-bold">{count}</span>
  </span>
);

// ------------------------------------------------------------
const OffenderRow = ({ row, onOpenTimeline }) => {
  const u = row.user || {};
  return (
    <tr className="hover:bg-slate-50" data-testid={`offender-row-${row.user_id}`}>
      <td className="px-3 py-3">
        <SeverityBadge severity={row.severity} />
      </td>
      <td className="px-3 py-3 min-w-[220px]">
        <div className="flex items-center gap-2">
          {u.seller_type === 'business' ? (
            <Building2 className="h-4 w-4 text-blue-600 shrink-0" />
          ) : (
            <User className="h-4 w-4 text-slate-500 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">
              {u.business_name || u.name || 'Unknown'}
            </div>
            <div className="text-xs text-slate-500 truncate">{u.email}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-center">
        <span className="text-lg font-bold text-slate-800">{row.total_score}</span>
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-1">
          {row.admin_deletes > 0 && (
            <IncidentChip label="admin-del" count={row.admin_deletes} tone="bg-red-50 border-red-300 text-red-800" />
          )}
          {row.admin_rejects > 0 && (
            <IncidentChip label="admin-rej" count={row.admin_rejects} tone="bg-orange-50 border-orange-300 text-orange-800" />
          )}
          {row.ai_holds > 0 && (
            <IncidentChip label="ai-hold" count={row.ai_holds} tone="bg-amber-50 border-amber-300 text-amber-800" />
          )}
          {row.self_deletes > 0 && (
            <IncidentChip label="self-del" count={row.self_deletes} tone="bg-blue-50 border-blue-300 text-blue-800" />
          )}
        </div>
      </td>
      <td className="px-3 py-3">
        <Badge
          variant="outline"
          className={
            u.trust_tier === 'GOLD' ? 'text-yellow-700 border-yellow-400'
            : u.trust_tier === 'VERIFIED' ? 'text-blue-700 border-blue-400'
            : u.trust_tier === 'BLOCKED' ? 'text-red-700 border-red-400'
            : 'text-slate-600'
          }
        >
          {u.trust_tier || 'UNVERIFIED'} · {u.trust_score ?? 0}
        </Badge>
      </td>
      <td className="px-3 py-3 text-xs text-slate-500">
        {row.latest_at ? new Date(row.latest_at).toLocaleString() : '-'}
      </td>
      <td className="px-3 py-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onOpenTimeline(row.user_id, u)}
          data-testid={`view-timeline-${row.user_id}`}
        >
          <Eye className="h-3.5 w-3.5 mr-1" /> Timeline
        </Button>
      </td>
    </tr>
  );
};

// ------------------------------------------------------------
const AlertCard = ({ alert, onAck, onResolve, onResend, onResendSlack, slackConfigured }) => {
  const u = alert.user || {};
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendingSlack, setResendingSlack] = useState(false);
  return (
    <div
      className={`p-3 rounded-lg border flex items-center justify-between gap-3 ${
        alert.severity === 'HIGH' ? 'bg-red-50 border-red-300'
        : alert.severity === 'MEDIUM' ? 'bg-amber-50 border-amber-300'
        : 'bg-slate-50 border-slate-300'
      }`}
      data-testid={`alert-card-${alert.id}`}
    >
      <div className="flex items-start gap-2 min-w-0 flex-1">
        <SeverityBadge severity={alert.severity} />
        <div className="min-w-0">
          <div className="text-sm text-slate-800 truncate">
            {alert.message}
          </div>
          <div className="text-xs text-slate-500 truncate flex items-center gap-1 flex-wrap">
            <span>{u.email || alert.user_id}</span>
            <span>·</span>
            <span>{alert.created_at?.slice(0, 19).replace('T', ' ')}</span>
            {alert.severity === 'HIGH' && (
              <>
                <span>·</span>
                <span
                  className="inline-flex items-center gap-0.5 text-emerald-700"
                  title={`Email sent to ${alert.notified_sent || 0} admin(s) at ${alert.notified_at || 'unknown time'}`}
                  data-testid={`notified-email-${alert.id}`}
                >
                  📧 {alert.notified_sent > 0 ? `sent (${alert.notified_sent})` : 'pending'}
                </span>
                {alert.slack_notified ? (
                  <span
                    className="inline-flex items-center gap-0.5 text-fuchsia-700"
                    title={`Slack posted at ${alert.slack_notified_at || 'unknown time'}`}
                    data-testid={`notified-slack-${alert.id}`}
                  >
                    · 💬 slack sent
                  </span>
                ) : slackConfigured ? (
                  <span className="inline-flex items-center gap-0.5 text-slate-400" title="Slack configured but this alert was not sent (may pre-date setup)">
                    · 💬 slack pending
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-0.5 text-slate-400" title="Slack webhook not configured - set SLACK_ALERT_WEBHOOK_URL in backend .env">
                    · 💬 slack off
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-1 shrink-0 flex-wrap justify-end">
        {alert.severity === 'HIGH' && (
          <>
            <Button
              size="sm" variant="outline"
              onClick={async () => { setResending(true); await onResend(alert.id); setResending(false); }}
              disabled={resending}
              data-testid={`resend-${alert.id}`}
              title="Resend the HIGH-severity admin email"
              className="text-xs h-7 px-2"
            >
              {resending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              📧 Resend
            </Button>
            {slackConfigured && (
              <Button
                size="sm" variant="outline"
                onClick={async () => { setResendingSlack(true); await onResendSlack(alert.id); setResendingSlack(false); }}
                disabled={resendingSlack}
                data-testid={`resend-slack-${alert.id}`}
                title="Resend this alert to Slack"
                className="text-xs h-7 px-2 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
              >
                {resendingSlack && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                💬 Slack
              </Button>
            )}
          </>
        )}
        <Button
          size="sm" variant="outline"
          onClick={async () => { setSaving(true); await onAck(alert.id); setSaving(false); }}
          disabled={saving}
          data-testid={`ack-${alert.id}`}
          className="text-xs h-7 px-2"
        >
          {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
          Ack
        </Button>
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7 px-2"
          onClick={async () => { setSaving(true); await onResolve(alert.id); setSaving(false); }}
          disabled={saving}
          data-testid={`resolve-${alert.id}`}
        >
          <CheckCircle2 className="h-3 w-3 mr-1" /> Resolve
        </Button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------
export default function AdminRepeatOffenders() {
  const navigate = useNavigate();
  const [offenders, setOffenders] = useState([]);
  const [stats, setStats] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [alertCounts, setAlertCounts] = useState({});
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [timelineUser, setTimelineUser] = useState(null); // {user_id, user_info}
  const [timeline, setTimeline] = useState([]);
  const [slackConfigured, setSlackConfigured] = useState(false);

  const token = () => localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [offRes, alRes, slackRes] = await Promise.all([
        axios.get(`${API}/api/trust/admin/repeat-offenders`, {
          headers: { Authorization: `Bearer ${token()}` }, params: { days },
        }),
        axios.get(`${API}/api/trust/admin/alerts`, {
          headers: { Authorization: `Bearer ${token()}` },
          params: { status_filter: 'open', limit: 30 },
        }),
        axios.get(`${API}/api/trust/admin/alerts/slack-status`, {
          headers: { Authorization: `Bearer ${token()}` },
        }).catch(() => ({ data: { configured: false } })),
      ]);
      setOffenders(offRes.data.offenders || []);
      setStats(offRes.data);
      setAlerts(alRes.data.alerts || []);
      setAlertCounts({
        high: alRes.data.open_high, medium: alRes.data.open_medium, low: alRes.data.open_low,
      });
      setSlackConfigured(!!slackRes.data.configured);
    } catch (e) {
      if (e.response?.status === 401) navigate('/login');
      else if (e.response?.status === 403) { toast.error('Admin only'); navigate('/'); }
      else toast.error('Failed to load repeat offenders');
    } finally { setLoading(false); }
  }, [days, navigate]);

  useEffect(() => { load(); }, [load]);

  const openTimeline = async (userId, userInfo) => {
    setTimelineUser({ user_id: userId, user: userInfo });
    try {
      const res = await axios.get(
        `${API}/api/trust/admin/repeat-offenders/${userId}/timeline`,
        { headers: { Authorization: `Bearer ${token()}` }, params: { days } },
      );
      setTimeline(res.data.incidents || []);
    } catch {
      toast.error('Failed to load timeline');
      setTimeline([]);
    }
  };

  const patchAlert = async (id, action) => {
    try {
      await axios.patch(
        `${API}/api/trust/admin/alerts/${id}?action=${action}`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      toast.success(action === 'ack' ? 'Acknowledged' : 'Resolved');
      await load();
    } catch { toast.error('Failed to update alert'); }
  };

  const resendEmail = async (id) => {
    try {
      const res = await axios.post(
        `${API}/api/trust/admin/alerts/${id}/resend-email`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      const r = res.data;
      if (r.skipped) {
        toast.warning(`Skipped: ${r.reason}`);
      } else {
        toast.success(`Resent to ${r.sent} recipient(s)`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    }
  };

  const resendSlack = async (id) => {
    try {
      const res = await axios.post(
        `${API}/api/trust/admin/alerts/${id}/resend-slack`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      if (res.data.sent) toast.success('Slack posted');
      else toast.warning(res.data.reason || res.data.error || 'Slack not configured');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Slack resend failed');
    }
  };

  const testSlack = async () => {
    try {
      const res = await axios.post(
        `${API}/api/trust/admin/alerts/slack-test`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      if (res.data.sent) toast.success('Test ping posted to Slack ✓');
      else toast.warning(res.data.reason || res.data.error || 'Slack test failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Slack test failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-repeat-offenders">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="repeat-offenders-title">
            <ShieldAlert className="h-6 w-6 text-red-600" />
            Repeat Offender Detection
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Sellers whose behaviour matches scam patterns - high self-delete rates
            (post → collect enquiries → vanish) or repeated admin removals for
            illegal content. Higher score = more urgent attention.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white"
            data-testid="offender-window-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="offenders-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">HIGH severity</div>
          <div className="text-2xl font-bold text-red-700" data-testid="stat-high">{stats.high ?? 0}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">MEDIUM</div>
          <div className="text-2xl font-bold text-amber-600" data-testid="stat-medium">{stats.medium ?? 0}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">LOW</div>
          <div className="text-2xl font-bold text-slate-600" data-testid="stat-low">{stats.low ?? 0}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1">
            <Bell className="h-3.5 w-3.5" /> Open alerts
          </div>
          <div className="text-2xl font-bold text-slate-800" data-testid="stat-alerts">
            {(alertCounts.high ?? 0) + (alertCounts.medium ?? 0) + (alertCounts.low ?? 0)}
          </div>
        </CardContent></Card>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
              <span className="flex items-center gap-2">
                <Bell className="h-4 w-4" /> Open alerts
              </span>
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={slackConfigured
                    ? 'border-fuchsia-400 text-fuchsia-700 bg-fuchsia-50'
                    : 'border-slate-300 text-slate-500'
                  }
                  data-testid="slack-status-badge"
                  title={slackConfigured
                    ? 'HIGH alerts will be mirrored to your Slack channel'
                    : 'Set SLACK_ALERT_WEBHOOK_URL in backend .env to enable Slack mirror'
                  }
                >
                  💬 Slack {slackConfigured ? 'connected' : 'off'}
                </Badge>
                {slackConfigured && (
                  <Button
                    size="sm" variant="outline"
                    onClick={testSlack}
                    data-testid="slack-test-btn"
                    className="h-7 text-xs border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
                  >
                    Send test ping
                  </Button>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.map((a) => (
              <AlertCard
                key={a.id}
                alert={a}
                slackConfigured={slackConfigured}
                onAck={(id) => patchAlert(id, 'ack')}
                onResolve={(id) => patchAlert(id, 'resolve')}
                onResend={resendEmail}
                onResendSlack={resendSlack}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Offenders table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Top offenders (rolling {days}d)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : offenders.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
              <p className="font-semibold text-slate-800">All clear</p>
              <p className="text-sm">No sellers cross the scam-signal threshold in this window.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 uppercase tracking-wide border-b bg-slate-50">
                  <th className="px-3 py-2 text-left">Severity</th>
                  <th className="px-3 py-2 text-left">Seller</th>
                  <th className="px-3 py-2 text-center">Score</th>
                  <th className="px-3 py-2 text-left">Incident mix</th>
                  <th className="px-3 py-2 text-left">Trust tier</th>
                  <th className="px-3 py-2 text-left">Latest</th>
                  <th className="px-3 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {offenders.map((row) => (
                  <OffenderRow key={row.user_id} row={row} onOpenTimeline={openTimeline} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Timeline drawer */}
      <Dialog open={!!timelineUser} onOpenChange={(o) => { if (!o) setTimelineUser(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-red-600" />
              Incident timeline
            </DialogTitle>
            <DialogDescription>
              {timelineUser?.user?.name || timelineUser?.user?.email || timelineUser?.user_id}
              {timelineUser?.user?.business_name && (
                <> · <span className="font-medium">{timelineUser.user.business_name}</span></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[500px] overflow-y-auto space-y-2 pt-2">
            {timeline.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">No incidents in window.</div>
            ) : timeline.map((t) => {
              const tone = t.incident_type.startsWith('admin_delete') ? 'border-red-300 bg-red-50'
                : t.incident_type.startsWith('admin_reject') ? 'border-orange-300 bg-orange-50'
                : t.incident_type.startsWith('ai_') ? 'border-amber-300 bg-amber-50'
                : 'border-blue-300 bg-blue-50';
              return (
                <div key={t.id} className={`p-3 rounded-lg border ${tone}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono font-semibold">{t.incident_type}</span>
                    <span className="text-slate-500">
                      weight <b>{t.weight}</b> · {t.created_at?.slice(0, 19).replace('T', ' ')}
                    </span>
                  </div>
                  {t.listing_title && (
                    <div className="text-sm text-slate-800 mt-1 truncate">
                      "{t.listing_title}"
                    </div>
                  )}
                  {t.metadata?.reason && (
                    <div className="text-xs text-slate-600 mt-1 italic">Reason: {t.metadata.reason}</div>
                  )}
                  {t.metadata?.trust_moderation?.hits?.length > 0 && (
                    <div className="text-[10px] text-slate-500 mt-1">
                      hits: {t.metadata.trust_moderation.hits.slice(0, 4).map((h) => `${h.category}("${h.match}")`).join(', ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
